/**
 * Desktop self-update trigger. The Tauri updater plugin is fully configured (endpoints + minisign
 * pubkey in `tauri.conf.json`, `updater:default` capability, `createUpdaterArtifacts`), registered in
 * `src-tauri/src/lib.rs` under `#[cfg(desktop)]`, and fed by the `scripts/release.sh` → GCS pipeline —
 * but nothing ever *calls* it, so a running app never polls `latest.json`. This module is that missing
 * runtime trigger: check on startup, download+install a newer release in the background, then surface a
 * toast offering a restart (the update applies on next launch regardless).
 *
 * Everything native is behind DYNAMIC imports (matching {@link ./shell.ts}) so the plugin code never
 * lands in the web bundle, and the check itself is dependency-injected (mirroring the DI pattern in
 * `src/auth/nativeOAuth.ts`) so tests need no Tauri runtime.
 */

/** A pending update, narrowed to just what the trigger needs from the plugin's `Update` object. */
export interface UpdateHandle {
  version: string;
  downloadAndInstall(onEvent?: (e: unknown) => void): Promise<void>;
}

/** Poll for an update. Resolves to a handle when one is available, or `null` when already current. */
export type CheckFn = () => Promise<UpdateHandle | null>;

export interface AutoUpdateDeps {
  /** `isTauri() && !isMobile()` — the updater plugin is registered only under `#[cfg(desktop)]`. */
  isDesktop: boolean;
  /** Injectable for tests; defaults to a dynamic import of the Tauri updater plugin. */
  check?: CheckFn;
  /** Called once an update has been downloaded + installed (e.g. push a "restart?" toast). */
  onReady?: (version: string) => void;
  /** Progress/diagnostic sink; defaults to a no-op. */
  log?: (msg: string) => void;
}

/**
 * Production {@link CheckFn}: dynamically import the updater plugin and probe the configured endpoint.
 * Kept out of the web bundle by the dynamic import; only ever reached when `isDesktop` is true.
 * Exported so the manual Settings flow ({@link ./useDesktopUpdater}) can reuse the exact same probe.
 */
export async function defaultCheck(): Promise<UpdateHandle | null> {
  const { check } = await import('@tauri-apps/plugin-updater');
  const update = await check();
  if (!update) return null;
  return {
    version: update.version,
    downloadAndInstall: (onEvent) => update.downloadAndInstall(onEvent),
  };
}

/** Outcome of a run, so callers/tests can assert what happened without inspecting side effects. */
export type AutoUpdateResult = 'skipped' | 'none' | 'installed' | 'error';

/**
 * Check for, download, and install a newer desktop release. Near-silent: it logs progress and, on
 * success, invokes `onReady` (the app turns that into a toast) — it never force-relaunches. Every
 * failure mode (offline, 404, DNS failure, signature mismatch, corrupt artifact) is swallowed and
 * reported as `'error'`; an update check must NEVER crash the app.
 */
export async function runAutoUpdate(deps: AutoUpdateDeps): Promise<AutoUpdateResult> {
  const { isDesktop, check = defaultCheck, onReady, log = () => {} } = deps;
  // Guard first — never touch the plugin off desktop; on Android/web it isn't registered and throws.
  if (!isDesktop) return 'skipped';
  try {
    const update = await check();
    if (!update) {
      log('up to date');
      return 'none';
    }
    log(`update ${update.version} available`);
    await update.downloadAndInstall((e) => log(`download progress: ${JSON.stringify(e)}`));
    log(`update ${update.version} installed; applies on next launch`);
    onReady?.(update.version);
    return 'installed';
  } catch (e) {
    // Offline / 404 / signature mismatch all land here — expected, non-fatal.
    log(`update check failed: ${(e as Error)?.message ?? String(e)}`);
    return 'error';
  }
}

/** A phase transition in the user-initiated update flow, emitted by {@link runManualUpdate}. */
export type ManualUpdatePhase = 'checking' | 'up-to-date' | 'downloading' | 'ready' | 'error';
export interface ManualUpdateEvent {
  phase: ManualUpdatePhase;
  /** Set on `downloading`/`ready`. */
  availableVersion?: string;
  /** Set on `error`. */
  error?: string;
}

/**
 * User-initiated update: check → download+install → ready, emitting each phase transition through
 * `emit`. Powers the Settings "Check for updates" control (see {@link ./useDesktopUpdater}). Unlike
 * {@link runAutoUpdate} it never toasts and surfaces intermediate progress; unlike a plain relaunch it
 * lets the UI offer the restart. Pure w.r.t. React (no hooks) so it's unit-tested without a renderer;
 * `check` is injected and defaults to the real endpoint probe. Every failure resolves as `error`.
 */
export async function runManualUpdate(emit: (e: ManualUpdateEvent) => void, check: CheckFn = defaultCheck): Promise<void> {
  emit({ phase: 'checking' });
  try {
    const update = await check();
    if (!update) {
      emit({ phase: 'up-to-date' });
      return;
    }
    emit({ phase: 'downloading', availableVersion: update.version });
    await update.downloadAndInstall();
    emit({ phase: 'ready', availableVersion: update.version });
  } catch (e) {
    // Offline / 404 / signature mismatch all land here — expected, never fatal.
    emit({ phase: 'error', error: (e as Error)?.message ?? String(e) });
  }
}

/**
 * Relaunch the app so a downloaded update applies immediately, instead of waiting for the next natural
 * launch. Behind a dynamic import of the process plugin (desktop-only). Only call from an explicit user
 * action (e.g. tapping the "restart" toast) — never force it mid-session.
 */
export async function relaunchApp(): Promise<void> {
  const { relaunch } = await import('@tauri-apps/plugin-process');
  await relaunch();
}

/**
 * The running app's version (from `tauri.conf.json`), for display in Settings. Behind a dynamic import
 * of the Tauri app API (desktop-only); resolves to null off-desktop or if the import/call fails, so a
 * caller can render "…" without special-casing.
 */
export async function getAppVersion(): Promise<string | null> {
  try {
    const { getVersion } = await import('@tauri-apps/api/app');
    return await getVersion();
  } catch {
    return null;
  }
}
